<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: Number,
    default: 0,
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  contactName: {
    type: String,
    default: '',
  },
  conversationDisplayId: {
    type: [String, Number],
    default: null,
  },
  createdAt: {
    type: [String, Date, Number],
    default: '',
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
});

const router = useRouter();

const itemUrl = computed(() =>
  frontendURL(`accounts/${props.accountId}/kanban`, { item: props.id })
);

const conversationUrl = computed(() =>
  props.conversationDisplayId
    ? frontendURL(
        `accounts/${props.accountId}/conversations/${props.conversationDisplayId}`
      )
    : null
);

const createdAtTime = computed(() =>
  props.createdAt ? dynamicTime(props.createdAt) : ''
);

const goToItem = () => router.push(itemUrl.value);
const goToConversation = () => {
  if (conversationUrl.value) router.push(conversationUrl.value);
};
</script>

<template>
  <CardLayout
    layout="col"
    class="[&>div]:gap-2 [&>div]:px-4 [&>div]:py-3 [&>div]:items-start"
  >
    <div class="flex items-center justify-between w-full gap-2">
      <div class="flex items-center min-w-0 gap-1.5">
        <Icon
          icon="i-ri-kanban-view"
          class="flex-shrink-0 text-n-slate-11 size-4"
        />
        <h5 class="m-0 text-sm truncate text-n-slate-12">
          {{ title || `#${id}` }}
        </h5>
      </div>
      <span
        v-if="createdAtTime"
        class="flex-shrink-0 text-sm text-n-slate-11"
      >
        {{ createdAtTime }}
      </span>
    </div>

    <div v-if="contactName" class="flex items-center gap-1.5 min-w-0">
      <Icon
        icon="i-ri-user-line"
        class="flex-shrink-0 text-n-slate-11 size-3.5"
      />
      <span class="text-sm truncate text-n-slate-12">{{ contactName }}</span>
    </div>

    <p v-if="description" class="m-0 text-sm text-n-slate-11 line-clamp-2">
      {{ description }}
    </p>

    <div class="flex items-center gap-2 mt-1">
      <NextButton
        v-if="conversationUrl"
        :label="$t('SEARCH.KANBAN.OPEN_CONVERSATION')"
        icon="i-ri-chat-3-line"
        sm
        outline
        slate
        @click="goToConversation"
      />
      <NextButton
        :label="$t('SEARCH.KANBAN.OPEN_ITEM')"
        icon="i-ri-kanban-view"
        sm
        @click="goToItem"
      />
    </div>
  </CardLayout>
</template>
